import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  IconButton, 
  Drawer, 
  List, 
  ListItem, 
  ListItemText, 
  useTheme, 
  useMediaQuery,
  Avatar,
  Chip,
  Menu,
  MenuItem,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  Lock as LockIcon,
  AccountBalanceWallet as WalletIcon,
  SwapHoriz as NetworkIcon,
  CheckCircle as ConnectedIcon,
  Error as ErrorIcon,
  WifiOff as DisconnectedIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { styled } from '@mui/material/styles';
import { useWallet } from '../context/WalletContext';
import { NETWORKS } from '../utils/contractUtils';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: 'linear-gradient(135deg, #000000 0%, #141414 100%)',
  backdropFilter: 'blur(20px)',
  borderBottom: '1px solid rgba(0, 113, 235, 0.3)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
  position: 'sticky',
  top: 0,
  zIndex: 1100,
}));

const NavButton = styled(Button)(({ theme, active }) => ({
  color: active ? '#0071EB' : '#FFFFFF',
  fontWeight: active ? 700 : 600,
  textTransform: 'none',
  fontSize: '16px',
  padding: '12px 20px',
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)',
  '&:hover': {
    background: active ? 'rgba(0, 113, 235, 0.1)' : 'rgba(255, 255, 255, 0.1)',
    transform: 'translateY(-2px)',
    color: '#0071EB',
    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)',
  },
}));

const ConnectButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #0071EB 0%, #0056B3 100%)',
  color: '#fff',
  fontWeight: 700,
  textTransform: 'none',
  borderRadius: '12px',
  padding: '12px 24px',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #0056B3 0%, #003D82 100%)',
    transform: 'translateY(-2px)',
  },
  '&:disabled': {
    background: 'rgba(128, 128, 128, 0.3)',
    transform: 'none',
  },
}));

const ConnectionStatusChip = styled(Chip)(({ status, theme }) => ({
  backgroundColor: status === 'connected' ? 'rgba(0, 255, 0, 0.2)' :
                   status === 'connecting' ? 'rgba(255, 165, 0, 0.2)' :
                   status === 'error' ? 'rgba(255, 0, 0, 0.2)' :
                   'rgba(128, 128, 128, 0.2)',
  color: status === 'connected' ? '#00FF00' :
         status === 'connecting' ? '#FFA500' :
         status === 'error' ? '#FF0000' :
         '#808080',
  border: `1px solid ${status === 'connected' ? '#00FF00' :
                      status === 'connecting' ? '#FFA500' :
                      status === 'error' ? '#FF0000' :
                      '#808080'}`,
  fontWeight: 600,
  '& .MuiChip-icon': {
    color: 'inherit',
  },
}));

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  
  const {
    provider,
    signer,
    address,
    isConnected,
    contract,
    currentNetwork,
    networkConfig,
    wallets,
    activeWallet,
    switchWallet,
    handleNetworkChange,
    connectWallet,
    disconnectWallet,
    isConnecting,
    connectionStatus,
    isInitialized,
    initError
  } = useWallet();

  const [networkMenuAnchor, setNetworkMenuAnchor] = useState(null);
  const [walletMenuAnchor, setWalletMenuAnchor] = useState(null);
  const [showConnectionAlert, setShowConnectionAlert] = useState(false);
  const [connectionAlertMessage, setConnectionAlertMessage] = useState('');

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Dashboard', path: '/dashboard' },
    { name: 'Lock Tokens', path: '/lock-tokens' },
    { name: 'My Tokens', path: '/locked-tokens' },
    { name: 'About', path: '/about' },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNetworkMenuOpen = (event) => {
    setNetworkMenuAnchor(event.currentTarget);
  };

  const handleNetworkMenuClose = () => {
    setNetworkMenuAnchor(null);
  };

  const handleWalletMenuOpen = (event) => {
    setWalletMenuAnchor(event.currentTarget);
  };

  const handleWalletMenuClose = () => {
    setWalletMenuAnchor(null);
  };

  const handleNetworkSwitch = async (newNetwork) => {
    try {
      await handleNetworkChange(newNetwork);
      handleNetworkMenuClose();
    } catch (error) {
      console.error('Network change failed:', error);
      setConnectionAlertMessage(`Failed to switch to ${NETWORKS[newNetwork].name}: ${error.message}`);
      setShowConnectionAlert(true);
    }
  };

  const handleWalletSwitch = (walletAddress) => {
    switchWallet(walletAddress);
    handleWalletMenuClose();
  };

  const handleConnectWallet = async () => {
    try {
      await connectWallet();
    } catch (error) {
      console.error('Wallet connection failed:', error);
      setConnectionAlertMessage(`Connection failed: ${error.message}`);
      setShowConnectionAlert(true);
    }
  };

  const handleDisconnectWallet = () => {
    disconnectWallet();
    handleWalletMenuClose();
  };

  const getConnectionStatusIcon = () => {
    switch (connectionStatus) {
      case 'connected':
        return <ConnectedIcon />;
      case 'connecting':
        return <CircularProgress size={16} />;
      case 'error':
        return <ErrorIcon />;
      default:
        return <DisconnectedIcon />;
    }
  };

  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'Connected';
      case 'connecting':
        return 'Connecting';
      case 'error':
        return 'Error';
      default:
        return 'Disconnected';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return '#4CAF50';
      case 'connecting':
        return '#FFA500';
      case 'error':
        return '#0071EB';
      default:
        return '#B3B3B3';
    }
  };

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'connected':
        return 'rgba(76, 175, 80, 0.1)';
      case 'connecting':
        return 'rgba(255, 165, 0, 0.1)';
      case 'error':
        return 'rgba(0, 113, 235, 0.1)';
      default:
        return 'rgba(179, 179, 179, 0.1)';
    }
  };

  return (
    <>
      <StyledAppBar position="static">
        <Toolbar sx={{ 
          minHeight: isMobile ? '56px' : '64px',
          px: isMobile ? 1 : 3
        }}>
          {/* Logo/Brand */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img
              src="/tokenlocker-logo.jpeg"
              alt="Token Locker Logo"
              style={{ height: isMobile ? 28 : 36, marginRight: isMobile ? 8 : 16 }}
            />
            <Typography 
              variant={isMobile ? 'h6' : 'h5'} 
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(90deg, #0071EB 0%, #0056B3 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                display: { xs: 'none', sm: 'block' }
              }}
            >
              Token Locker
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(90deg, #0071EB 0%, #0056B3 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                display: { xs: 'block', sm: 'none' }
              }}
            >
              TL
            </Typography>
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {navItems.map((item) => (
                <NavButton
                  key={item.name}
                  component={Link}
                  to={item.path}
                  active={location.pathname === item.path ? 1 : 0}
                  sx={{
                    position: 'relative'
                  }}
                >
                  {item.name}
                </NavButton>
              ))}
            </Box>
          )}

          {/* Connection Status & Wallet Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.5 : 1, ml: isMobile ? 1 : 2 }}>
            {/* Connection Status */}
            <ConnectionStatusChip
              status={connectionStatus}
              icon={getConnectionStatusIcon()}
              label={isMobile ? '' : connectionStatus}
              size={isMobile ? 'small' : 'medium'}
            />

            {/* Network Selector */}
            <Tooltip title={`Current: ${networkConfig?.name || 'Unknown'}`}>
              <Button
                onClick={handleNetworkMenuOpen}
                startIcon={<NetworkIcon />}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  color: '#0071EB',
                  minWidth: isMobile ? 'auto' : 'auto',
                  px: isMobile ? 1 : 2,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 113, 235, 0.1)',
                  }
                }}
              >
                {!isMobile && (networkConfig?.name || 'Network')}
              </Button>
            </Tooltip>

            {/* Wallet Connection */}
            {isConnected ? (
              <Tooltip title="Wallet Menu">
                <Button
                  onClick={handleWalletMenuOpen}
                  startIcon={<WalletIcon />}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    color: '#0071EB',
                    minWidth: isMobile ? 'auto' : 'auto',
                    px: isMobile ? 1 : 2,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 113, 235, 0.1)',
                    }
                  }}
                >
                  {!isMobile && `${address?.slice(0, 6)}...${address?.slice(-4)}`}
                </Button>
              </Tooltip>
            ) : (
              <ConnectButton
                onClick={handleConnectWallet}
                disabled={isConnecting}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  fontSize: isMobile ? '12px' : '14px',
                  px: isMobile ? 2 : 3,
                  py: isMobile ? 0.5 : 1
                }}
              >
                {isConnecting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  isMobile ? 'Connect' : 'Connect Wallet'
                )}
              </ConnectButton>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="end"
                onClick={handleDrawerToggle}
                sx={{ ml: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </StyledAppBar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 280,
            background: 'linear-gradient(135deg, #000000 0%, #141414 100%)',
            borderLeft: '1px solid rgba(0, 113, 235, 0.3)'
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#0071EB', 
              fontWeight: 700, 
              mb: 3,
              textAlign: 'center',
              background: 'linear-gradient(90deg, #0071EB 0%, #FFA500 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Navigation Menu
          </Typography>
          <List>
            {navItems.map((item) => (
              <ListItem 
                key={item.name} 
                component={Link} 
                to={item.path}
                onClick={handleDrawerToggle}
                sx={{
                  color: location.pathname === item.path ? '#0071EB' : '#B3B3B3',
                  fontWeight: location.pathname === item.path ? 700 : 400,
                  borderRadius: 2,
                  mb: 1,
                  py: 1.5,
                  px: 2,
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 113, 235, 0.1)',
                    color: '#0071EB',
                    transform: 'translateX(4px)',
                    transition: 'all 0.3s ease'
                  },
                  '&::before': location.pathname === item.path ? {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '4px',
                    background: 'linear-gradient(135deg, #0071EB 0%, #0056B3 100%)',
                    borderRadius: '0 2px 2px 0'
                  } : {}
                }}
              >
                <ListItemText 
                  primary={item.name}
                  primaryTypographyProps={{
                    fontSize: '16px',
                    fontWeight: location.pathname === item.path ? 700 : 500,
                    color: location.pathname === item.path ? '#0071EB' : '#FFFFFF',
                    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)'
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Network Menu */}
      <Menu
        anchorEl={networkMenuAnchor}
        open={Boolean(networkMenuAnchor)}
        onClose={handleNetworkMenuClose}
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #141414 0%, rgba(20, 20, 20, 0.95) 100%)',
            border: '1px solid rgba(229, 9, 20, 0.3)',
            borderRadius: 2,
            mt: 1
          }
        }}
      >
        {Object.entries(NETWORKS).map(([key, network]) => (
          <MenuItem
            key={key}
            onClick={() => handleNetworkSwitch(key)}
            selected={currentNetwork === key}
            sx={{
              color: currentNetwork === key ? '#0071EB' : '#B3B3B3',
              fontWeight: currentNetwork === key ? 700 : 400,
              '&:hover': {
                backgroundColor: 'rgba(0, 113, 235, 0.1)',
                color: '#0071EB'
              }
            }}
          >
            {network.name}
          </MenuItem>
        ))}
      </Menu>

      {/* Wallet Menu */}
      <Menu
        anchorEl={walletMenuAnchor}
        open={Boolean(walletMenuAnchor)}
        onClose={handleWalletMenuClose}
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #141414 0%, rgba(20, 20, 20, 0.95) 100%)',
            border: '1px solid rgba(229, 9, 20, 0.3)',
            borderRadius: 2,
            mt: 1
          }
        }}
      >
        {wallets.map((wallet) => (
          <MenuItem
            key={wallet.address}
            onClick={() => handleWalletSwitch(wallet.address)}
            selected={activeWallet === wallet.address}
            sx={{
              color: activeWallet === wallet.address ? '#0071EB' : '#B3B3B3',
              fontWeight: activeWallet === wallet.address ? 700 : 400,
              '&:hover': {
                backgroundColor: 'rgba(0, 113, 235, 0.1)',
                color: '#0071EB'
              }
            }}
          >
            {`${wallet.address.slice(0, 6)}...${wallet.address.slice(-4)}`}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default Navbar;